import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  FaArrowLeft, FaSave, FaTimes, FaUser, FaPhone,
  FaMapMarkerAlt, FaUsers, FaSpinner, FaRing,
  FaPaperclip, FaTrash, FaDownload, FaFilePdf,
  FaFileImage, FaFileWord, FaFile, FaPlus, FaChartBar,
  FaBookOpen, FaCheckCircle, FaTimesCircle, FaClock, FaHistory,
  FaExchangeAlt, FaUndoAlt, FaHome
} from 'react-icons/fa';
import api, { congregacaoService, logAlteracaoService, reuniaoCriancasService } from '../../services/api';
import SeletorResponsaveis from '../../components/SeletorResponsaveis';
import './CadastroIrmandade.css';

// ── helpers ──────────────────────────────────────────────────────────────────
const mascaraCPF = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0,3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}`;
  return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`;
};
function calcularIdade(dataNascimento) {
  if (!dataNascimento) return null;
  const hoje = new Date();
  const nasc = new Date(dataNascimento);
  let idade = hoje.getFullYear() - nasc.getFullYear();
  if (hoje.getMonth() < nasc.getMonth() ||
     (hoje.getMonth() === nasc.getMonth() && hoje.getDate() < nasc.getDate())) idade--;
  return idade;
}

function determinarCategoria(dataNascimento, estadoCivil) {
  const idade = calcularIdade(dataNascimento);
  if (idade === null) return null;
  if (idade < 12) return 'crianca';
  if (estadoCivil === 'casado' || estadoCivil === 'divorciado' || estadoCivil === 'viuvo') return 'adulto';
  return 'jovem';
}

const valorTriState = (value) => value === true ? 'true' : value === false ? 'false' : 'null';
const parseTriState = (value) => value === 'true' ? true : value === 'false' ? false : null;

function iconeDoc(mime) {
  if (!mime) return <FaFile />;
  if (mime.includes('pdf'))   return <FaFilePdf   style={{ color: '#e53e3e' }} />;
  if (mime.includes('image')) return <FaFileImage  style={{ color: '#3182ce' }} />;
  if (mime.includes('word') || mime.includes('document')) return <FaFileWord style={{ color: '#2b6cb0' }} />;
  return <FaFile />;
}

function formatBytes(b) {
  if (!b) return '';
  if (b < 1024) return b + ' B';
  if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
  return (b / (1024 * 1024)).toFixed(1) + ' MB';
}

function precisaResponsavel(idade, responsaveis = []) {
  if (responsaveis.length > 0) return true;
  return idade !== null && idade <= 12;
}

// ── Componente principal ──────────────────────────────────────────────────────
const EditarIrmandade = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { canWriteModule } = useAuth();

  // Bloqueia acesso direto à rota de edição sem permissão
  useEffect(() => {
    if (!canWriteModule('irmandade')) {
      navigate('/irmandade', { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [carregando,          setCarregando]          = useState(false);
  const [carregandoMembro,    setCarregandoMembro]    = useState(true);
  const [cargos,              setCargos]              = useState([]);
  const [carregandoCargos,    setCarregandoCargos]    = useState(false);
  const [potenciaisResponsaveis, setPotenciaisResponsaveis] = useState([]);
  const [carregandoResponsaveis, setCarregandoResponsaveis] = useState(false);
  const [congregacoes, setCongregacoes] = useState([]);
  const [loadingCongregacoes, setLoadingCongregacoes] = useState(true);
  const [cargosAtuais,        setCargosAtuais]        = useState([]);
  const [documentos,          setDocumentos]          = useState([]);
  const [uploadInfo,          setUploadInfo]          = useState({ nome: '', arquivo: null });
  const [enviandoDoc,         setEnviandoDoc]         = useState(false);
  const [historicoPresencas,    setHistoricoPresencas]    = useState([]);
  const [loadingHistorico,      setLoadingHistorico]      = useState(false);
  const [historicoRecitativos,  setHistoricoRecitativos]  = useState(null);
  const [loadingHistRec,        setLoadingHistRec]        = useState(false);
  const [logsAlteracao,         setLogsAlteracao]         = useState([]);
  const [loadingLogs,           setLoadingLogs]           = useState(false);
  const [logsExpandido,         setLogsExpandido]         = useState(false);
  const [freqCriancas,          setFreqCriancas]          = useState(null);
  const [loadingFreqCriancas,   setLoadingFreqCriancas]   = useState(false);
  const [situacao,              setSituacao]              = useState({ mudouSe: false, dataMudouSe: null });
  const [alterandoSituacao,     setAlterandoSituacao]     = useState(false);
  const fileRef = useRef();

  // Localização / geocodificação
  const [coords,              setCoords]              = useState({ lat: null, lng: null });
  const [geocodificando,      setGeocodificando]      = useState(false);
  const [geoMensagem,         setGeoMensagem]         = useState(null); // { tipo: 'ok'|'erro', texto }
  const [coordsManuais,       setCoordsManuais]       = useState({ lat: '', lng: '' });
  const [mostrarCoordsManuais, setMostrarCoordsManuais] = useState(false);

  const [formData, setFormData] = useState({
    nome: '',
    cpf: '',
    data_nascimento: '',
    telefone: '',
    email: '',
    endereco: '',
    congregacao: '',
    congregacao_outra: '',
    tipo: 'irmao',
    estado_civil: 'solteiro',
    responsavel_id: '',
    responsaveis: [],
    observacoes: '',
    comparece_reuniao: null,
    frequente_reuniao: null,
    batizado: null,
    visita_indicada: null,
    aceitou_visita: null,
    cargos_selecionados: []
  });

  const [erros, setErros] = useState({});

  const carregarCongregacoes = async () => {
    try {
      setLoadingCongregacoes(true);
      const response = await congregacaoService.getAtivas();
      if (response.success && response.data) setCongregacoes(response.data);
    } catch (error) {
      console.error('Erro ao carregar congregações:', error);
    } finally {
      setLoadingCongregacoes(false);
    }
  };

  useEffect(() => {
    if (id) {
      carregarMembro();
      carregarCargos();
      carregarPotenciaisResponsaveis();
      carregarCongregacoes();
      carregarCargosDoMembro();
      carregarDocumentos();
      carregarHistoricoPresencas();
      carregarHistoricoRecitativos();
      carregarLogs();
      carregarFreqCriancas();
    }
  }, [id]); // eslint-disable-line

  // ── Carregamento de dados ─────────────────────────────────────────────────
  const carregarMembro = async () => {
    try {
      setCarregandoMembro(true);
      const { data: m } = await api.get(`/irmandade/${id}`);
      const cpfRaw = m.cpf || '';
      const vinculos = Array.isArray(m.responsaveis_vinculos) ? m.responsaveis_vinculos
        : Array.isArray(m.responsaveisVinculos) ? m.responsaveisVinculos : [];
      let responsaveis = vinculos.map(v => ({
        id: v.responsavel_id ?? v.responsavel?.id,
        principal: !!v.principal,
      })).filter(r => r.id != null);
      // Fallback: se não há vínculos mas existe responsavel_id antigo, usa-o como principal
      if (responsaveis.length === 0 && m.responsavel_id) {
        responsaveis = [{ id: m.responsavel_id, principal: true }];
      }
      const respObjetos = vinculos
        .map(v => v.responsavel)
        .filter(Boolean)
        .map(r => ({
          id: r.id,
          nome_completo: r.nome_completo || r.nomeCompleto,
          nomeCompleto: r.nome_completo || r.nomeCompleto,
        }));
      if (respObjetos.length === 0 && m.responsavel) {
        respObjetos.push({
          id: m.responsavel.id,
          nome_completo: m.responsavel.nome_completo || m.responsavel.nomeCompleto,
          nomeCompleto: m.responsavel.nome_completo || m.responsavel.nomeCompleto,
        });
      }
      setFormData({
        nome:           m.nome_completo || m.nomeCompleto || '',
        cpf:            mascaraCPF(cpfRaw),
        data_nascimento: m.data_nascimento ? m.data_nascimento.split('T')[0] : '',
        telefone:       m.telefone       || '',
        email:          m.email          || '',
        endereco:       m.endereco       || '',
        congregacao:    m.congregacao    || '',
        tipo:           m.tipo_pessoa    || 'irmao',
        estado_civil:   m.estado_civil   || m.estadoCivil || (m.casado ? 'casado' : 'solteiro'),
        responsavel_id: m.responsavel_id || '',
        responsaveis,
        observacoes:    m.observacoes    || '',
        comparece_reuniao: m.comparece_reuniao ?? m.compareceReuniao ?? null,
        frequente_reuniao: m.frequente_reuniao ?? m.frequenteReuniao ?? null,
        batizado: m.batizado ?? null,
        visita_indicada: m.visita_indicada ?? m.visitaIndicada ?? null,
        aceitou_visita: m.aceitou_visita ?? m.aceitouVisita ?? null,
        cargos_selecionados: []
      });
      setSituacao({ mudouSe: !!m.mudou_se, dataMudouSe: m.data_mudou_se || null });
      setCoords({ lat: m.latitude ?? null, lng: m.longitude ?? null });
      if (respObjetos.length > 0) {
        setPotenciaisResponsaveis(prev => {
          const ids = new Set(prev.map(p => Number(p.id)));
          const extras = respObjetos.filter(r => !ids.has(Number(r.id)));
          return extras.length ? [...prev, ...extras] : prev;
        });
      }
    } catch {
      setErros({ geral: 'Erro ao carregar dados do membro.' });
    } finally {
      setCarregandoMembro(false);
    }
  };

  const carregarCargos = async () => {
    try {
      setCarregandoCargos(true);
      const { data } = await api.get('/cargos');
      setCargos(data);
    } catch { /* silencioso */ } finally { setCarregandoCargos(false); }
  };

  const carregarPotenciaisResponsaveis = async () => {
    try {
      setCarregandoResponsaveis(true);
      const { data } = await api.get('/irmandade/potenciais-responsaveis');
      setPotenciaisResponsaveis(data.filter(r => r.id !== parseInt(id)));
    } catch { /* silencioso */ } finally { setCarregandoResponsaveis(false); }
  };

  const carregarCargosDoMembro = async () => {
    try {
      const { data } = await api.get(`/irmandade/${id}/cargos`);
      const ids = data.map(c => c.id);
      setCargosAtuais(ids);
      setFormData(prev => ({ ...prev, cargos_selecionados: ids }));
    } catch { /* silencioso */ }
  };

  const carregarDocumentos = async () => {
    try {
      const { data } = await api.get(`/irmandade/${id}/documentos`);
      setDocumentos(data);
    } catch { /* silencioso */ }
  };

  const carregarHistoricoRecitativos = async () => {
    try {
      setLoadingHistRec(true);
      const { data } = await api.get(`/irmandade/${id}/historico-recitativos`);
      if (data.success) setHistoricoRecitativos(data.data);
    } catch { /* silencioso */ } finally {
      setLoadingHistRec(false);
    }
  };

  const carregarFreqCriancas = async () => {
    try {
      setLoadingFreqCriancas(true);
      const data = await reuniaoCriancasService.frequenciaMembro(id);
      setFreqCriancas(data);
    } catch { /* silencioso */ } finally {
      setLoadingFreqCriancas(false);
    }
  };

  const carregarLogs = async () => {
    try {
      setLoadingLogs(true);
      const data = await logAlteracaoService.listar(id);
      setLogsAlteracao(Array.isArray(data) ? data : []);
    } catch { /* silencioso */ } finally {
      setLoadingLogs(false);
    }
  };

  const carregarHistoricoPresencas = async () => {
    try {
      setLoadingHistorico(true);
      const { data } = await api.get(`/irmandade/${id}/historico-presencas`);
      setHistoricoPresencas(data?.data || []);
    } catch { /* silencioso */ } finally {
      setLoadingHistorico(false);
    }
  };

  // ── Geocodificação ────────────────────────────────────────────────────────
  const handleGeocodificar = async () => {
    setGeocodificando(true);
    setGeoMensagem(null);
    try {
      const { data } = await api.post(`/irmandade/${id}/geocodificar`);
      setCoords({ lat: data.latitude, lng: data.longitude });
      const fonteTexto = data.fonte === 'manual' ? 'manual' : data.fonte === 'nominatim' ? 'OpenStreetMap' : `bairro "${data.bairro}"`;
      setGeoMensagem({ tipo: 'ok', texto: `Localização encontrada via ${fonteTexto}: ${data.latitude.toFixed(5)}, ${data.longitude.toFixed(5)}` });
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao geocodificar. Tente ajustar o endereço.';
      setGeoMensagem({ tipo: 'erro', texto: msg });
    } finally {
      setGeocodificando(false);
    }
  };

  const handleSalvarCoordsManuais = async () => {
    const lat = parseFloat(coordsManuais.lat);
    const lng = parseFloat(coordsManuais.lng);
    if (isNaN(lat) || isNaN(lng) || lat < -34 || lat > 5 || lng < -74 || lng > -28) {
      setGeoMensagem({ tipo: 'erro', texto: 'Coordenadas inválidas para o Brasil. Latitude: -34 a 5, Longitude: -74 a -28.' });
      return;
    }
    setGeocodificando(true);
    setGeoMensagem(null);
    try {
      await api.post(`/irmandade/${id}/geocodificar`, { latitude: lat, longitude: lng });
      setCoords({ lat, lng });
      setMostrarCoordsManuais(false);
      setCoordsManuais({ lat: '', lng: '' });
      setGeoMensagem({ tipo: 'ok', texto: `Coordenadas salvas manualmente: ${lat.toFixed(5)}, ${lng.toFixed(5)}` });
    } catch {
      setGeoMensagem({ tipo: 'erro', texto: 'Erro ao salvar coordenadas.' });
    } finally {
      setGeocodificando(false);
    }
  };

  // ── Situação (mudou-se) ────────────────────────────────────────────────────
  const handleMarcarMudouSe = async () => {
    if (!window.confirm('Marcar como "mudou-se"? A pessoa ficará inativa: não será contada em estatísticas, recitativos, reunião de crianças, visitas nem no mapa. O cadastro continua disponível e pode ser reativado depois.')) return;
    setAlterandoSituacao(true);
    try {
      await api.patch(`/irmandade/${id}/mudou-se`);
      setSituacao({ mudouSe: true, dataMudouSe: new Date().toISOString() });
    } catch {
      alert('Erro ao marcar como mudou-se.');
    } finally {
      setAlterandoSituacao(false);
    }
  };

  const handleReativar = async () => {
    setAlterandoSituacao(true);
    try {
      await api.patch(`/irmandade/${id}/reativar`);
      setSituacao({ mudouSe: false, dataMudouSe: null });
    } catch {
      alert('Erro ao reativar membro.');
    } finally {
      setAlterandoSituacao(false);
    }
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (erros[name]) setErros(prev => { const n = { ...prev }; delete n[name]; return n; });
  };

  const handleCargoChange = (cargoId) => {
    setFormData(prev => {
      const list = prev.cargos_selecionados;
      return {
        ...prev,
        cargos_selecionados: list.includes(cargoId)
          ? list.filter(c => c !== cargoId)
          : [...list, cargoId]
      };
    });
  };

  // ── Upload de documento ───────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadInfo({ nome: file.name.replace(/\.[^.]+$/, ''), arquivo: file });
  };

  const handleUpload = async () => {
    if (!uploadInfo.arquivo) return;
    setEnviandoDoc(true);
    try {
      const form = new FormData();
      form.append('arquivo', uploadInfo.arquivo);
      form.append('nome', uploadInfo.nome || uploadInfo.arquivo.name);
      await api.post(`/irmandade/${id}/documentos`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadInfo({ nome: '', arquivo: null });
      if (fileRef.current) fileRef.current.value = '';
      await carregarDocumentos();
    } catch (err) {
      alert(err?.response?.data?.error || 'Erro ao enviar documento.');
    } finally {
      setEnviandoDoc(false);
    }
  };

  const handleExcluirDoc = async (docId) => {
    if (!window.confirm('Excluir este documento?')) return;
    try {
      await api.delete(`/irmandade/${id}/documentos/${docId}`);
      setDocumentos(prev => prev.filter(d => d.id !== docId));
    } catch {
      alert('Erro ao excluir documento.');
    }
  };

  const handleDownloadDoc = (docId) => {
    const baseURL = `${window.location.protocol}//${window.location.hostname}:3001`;
    const token = localStorage.getItem('token');
    // Abre em nova aba (token via header não funciona em <a> simples, usa fetch + blob)
    fetch(`${baseURL}/api/irmandade/${id}/documentos/${docId}/download`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.blob())
      .then(blob => {
        const doc = documentos.find(d => d.id === docId);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = doc?.nome || 'documento'; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => alert('Erro ao baixar documento.'));
  };

  // ── Validação e submit ────────────────────────────────────────────────────
  const validarFormulario = () => {
    const errs = {};
    if (!formData.nome.trim()) errs.nome = 'Nome é obrigatório';
    if (!formData.data_nascimento) {
      errs.data_nascimento = 'Data de nascimento é obrigatória';
    } else if (calcularIdade(formData.data_nascimento) === null) {
      errs.data_nascimento = 'Data inválida';
    }
    const congregacaoValor = formData.congregacao === '__outra__' ? formData.congregacao_outra?.trim() : formData.congregacao?.trim();
    if (!congregacaoValor) errs.congregacao = formData.congregacao === '__outra__' ? 'Informe o nome da congregação' : 'Congregação é obrigatória';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      errs.email = 'Email inválido';
    setErros(errs);
    return Object.keys(errs).length === 0;
  };

  const gerenciarCargos = async (membroId, categoria) => {
    if (categoria === 'crianca') return;
    const antigos = new Set(cargosAtuais);
    const novos   = new Set(formData.cargos_selecionados);
    for (const cId of antigos) if (!novos.has(cId)) await api.delete(`/irmandade/${membroId}/cargos/${cId}`).catch(() => {});
    const idsParaAdicionar = [...novos].filter(cId => !antigos.has(cId));
    if (idsParaAdicionar.length > 0) await api.post(`/irmandade/${membroId}/cargos`, { cargo_ids: idsParaAdicionar }).catch(() => {});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validarFormulario()) return;
    try {
      setCarregando(true);
      const categoria = determinarCategoria(formData.data_nascimento, formData.estado_civil);
      await api.put(`/irmandade/${id}`, {
        nome_completo:  formData.nome,
        cpf:            formData.cpf.replace(/\D/g, '') || null,
        data_nascimento: formData.data_nascimento,
        tipo_pessoa:    formData.tipo,
        telefone:       formData.telefone,
        email:          formData.email,
        endereco:       formData.endereco,
        congregacao:    formData.congregacao === '__outra__' ? (formData.congregacao_outra || '').trim() : formData.congregacao,
        estado_civil:   formData.estado_civil,
        responsavel_id: formData.responsaveis.find(r => r.principal)?.id ?? formData.responsaveis[0]?.id ?? null,
        responsaveis:   formData.responsaveis,
        observacoes:    formData.observacoes,
        categoria,
      });
      await api.patch(`/irmandade/${id}/frequencia`, {
        comparece_reuniao: formData.comparece_reuniao,
        frequente_reuniao: formData.frequente_reuniao,
        visita_indicada: formData.visita_indicada,
        aceitou_visita: formData.aceitou_visita,
        ...(categoria === 'jovem' ? { batizado: formData.batizado } : {}),
      });
      await gerenciarCargos(id, categoria);
      navigate('/irmandade');
    } catch (err) {
      const msg = err.response?.data?.details || err.response?.data?.error || err.response?.data?.message || 'Erro ao atualizar membro.';
      setErros({ geral: msg });
    } finally {
      setCarregando(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (carregandoMembro) {
    return (
      <div className="cadastro-irmandade">
        <div className="carregando-container">
          <FaSpinner className="spinner" />
          <p>Carregando dados do membro...</p>
        </div>
      </div>
    );
  }

  const idade      = calcularIdade(formData.data_nascimento);
  const categoria  = determinarCategoria(formData.data_nascimento, formData.estado_civil);
  const precisaResp = precisaResponsavel(idade, formData.responsaveis);
  const temCargos   = categoria === 'jovem' || categoria === 'adulto';

  const catLabel = { crianca: 'Criança', jovem: 'Jovem / Mocidade', adulto: 'Adulto' };
  const ecLabel  = { solteiro: 'Solteiro(a) — Jovem/Mocidade', casado: 'Casado(a) — Adulto', divorciado: 'Divorciado(a) — Adulto', viuvo: 'Viúvo(a) — Adulto' };

  return (
    <div className="cadastro-irmandade">
      <div className="cadastro-header">
        <button type="button" className="btn-voltar" onClick={() => navigate('/irmandade')}>
          <FaArrowLeft /> Voltar
        </button>
        <h1><FaUser /> Editar Membro da Irmandade</h1>
      </div>

      <form onSubmit={handleSubmit} className="cadastro-form">
        {erros.geral && <div className="erro-geral">{erros.geral}</div>}

        {/* ── Informações pessoais ── */}
        <div className="form-section">
          <h3><FaUser /> Informações Pessoais</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="nome">Nome Completo *</label>
              <input id="nome" name="nome" type="text"
                value={formData.nome} onChange={handleInputChange}
                className={erros.nome ? 'erro' : ''}
                placeholder="Nome completo" />
              {erros.nome && <span className="erro-texto">{erros.nome}</span>}
            </div>

            <div className="form-group" style={{ maxWidth: 200 }}>
              <label htmlFor="cpf">CPF</label>
              <input
                type="text"
                id="cpf"
                name="cpf"
                value={formData.cpf}
                onChange={e => setFormData(prev => ({ ...prev, cpf: mascaraCPF(e.target.value) }))}
                placeholder="000.000.000-00"
                maxLength={14}
                inputMode="numeric"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="data_nascimento">Data de Nascimento *</label>
              <input id="data_nascimento" name="data_nascimento" type="date"
                value={formData.data_nascimento} onChange={handleInputChange}
                className={erros.data_nascimento ? 'erro' : ''} />
              {erros.data_nascimento && <span className="erro-texto">{erros.data_nascimento}</span>}
              {idade !== null && (
                <span className="idade-info">
                  {idade} anos —{' '}
                  <strong style={{ color: categoria === 'crianca' ? '#3182ce' : categoria === 'jovem' ? '#6b46c1' : '#2d6a4f' }}>
                    {catLabel[categoria]}
                  </strong>
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="tipo">Gênero</label>
              <select id="tipo" name="tipo" value={formData.tipo} onChange={handleInputChange}>
                <option value="irmao">Irmão</option>
                <option value="irma">Irmã</option>
              </select>
            </div>
          </div>

          {/* Estado Civil */}
          {categoria !== 'crianca' && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="estado_civil">
                  <FaRing style={{ marginRight: 6 }} />
                  Estado Civil
                  <span style={{ fontSize: '.78rem', color: '#6b7280', marginLeft: 8 }}>
                    (define se é Jovem ou Adulto)
                  </span>
                </label>
                <select id="estado_civil" name="estado_civil"
                  value={formData.estado_civil} onChange={handleInputChange}
                  style={{ fontWeight: 500 }}>
                  <option value="solteiro">{ecLabel.solteiro}</option>
                  <option value="casado">{ecLabel.casado}</option>
                  <option value="divorciado">{ecLabel.divorciado}</option>
                  <option value="viuvo">{ecLabel.viuvo}</option>
                </select>
                <span className="campo-dica">
                  Solteiro(a) de qualquer idade → Jovem/Mocidade.
                  Casado(a), divorciado(a) ou viúvo(a) → Adulto.
                </span>
              </div>
            </div>
          )}
        </div>

        {/* ── Contato ── */}
        <div className="form-section">
          <h3><FaPhone /> Contato</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="telefone">Telefone</label>
              <input id="telefone" name="telefone" type="tel"
                value={formData.telefone} onChange={handleInputChange}
                placeholder="(00) 00000-0000" />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input id="email" name="email" type="email"
                value={formData.email} onChange={handleInputChange}
                className={erros.email ? 'erro' : ''}
                placeholder="email@exemplo.com" />
              {erros.email && <span className="erro-texto">{erros.email}</span>}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="endereco">Endereço</label>
              <input id="endereco" name="endereco" type="text"
                value={formData.endereco} onChange={handleInputChange}
                placeholder="Rua, número, bairro, cidade" />
            </div>
          </div>

          {/* ── Localização no mapa ── */}
          <div className="geo-painel">
            <div className="geo-painel-header">
              <FaMapMarkerAlt className="geo-icon" />
              <div className="geo-info">
                <span className="geo-titulo">Localização no mapa</span>
                {coords.lat && coords.lng ? (
                  <span className="geo-coords-texto">
                    {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}
                    <a
                      href={`https://www.google.com/maps?q=${coords.lat},${coords.lng}`}
                      target="_blank"
                      rel="noreferrer"
                      className="geo-link-gmaps"
                      title="Ver no Google Maps"
                    >
                      Ver no mapa ↗
                    </a>
                  </span>
                ) : (
                  <span className="geo-sem-coords">Sem localização definida</span>
                )}
              </div>
              <div className="geo-acoes">
                <button
                  type="button"
                  className="geo-btn geo-btn-auto"
                  onClick={handleGeocodificar}
                  disabled={geocodificando || !formData.endereco}
                  title="Buscar coordenadas automaticamente a partir do endereço"
                >
                  {geocodificando ? '⏳ Buscando…' : '📍 Geocodificar'}
                </button>
                <button
                  type="button"
                  className="geo-btn geo-btn-manual"
                  onClick={() => setMostrarCoordsManuais(v => !v)}
                  title="Informar coordenadas manualmente"
                >
                  ✏️ Manual
                </button>
              </div>
            </div>

            {mostrarCoordsManuais && (
              <div className="geo-manual">
                <p className="geo-manual-dica">
                  Abra o <a href={`https://www.google.com/maps?q=${encodeURIComponent((formData.endereco || '') + ', Areiópolis SP')}`} target="_blank" rel="noreferrer">Google Maps ↗</a>,
                  clique no local exato e copie as coordenadas (ex.: <em>-22.67123, -48.66045</em>).
                </p>
                <div className="geo-manual-inputs">
                  <input
                    type="text"
                    placeholder="Latitude (ex: -22.67123)"
                    value={coordsManuais.lat}
                    onChange={e => setCoordsManuais(v => ({ ...v, lat: e.target.value }))}
                    className="geo-input"
                  />
                  <input
                    type="text"
                    placeholder="Longitude (ex: -48.66045)"
                    value={coordsManuais.lng}
                    onChange={e => setCoordsManuais(v => ({ ...v, lng: e.target.value }))}
                    className="geo-input"
                  />
                  <button
                    type="button"
                    className="geo-btn geo-btn-salvar"
                    onClick={handleSalvarCoordsManuais}
                    disabled={geocodificando}
                  >
                    Salvar
                  </button>
                </div>
              </div>
            )}

            {geoMensagem && (
              <div className={`geo-mensagem geo-mensagem-${geoMensagem.tipo}`}>
                {geoMensagem.tipo === 'ok' ? '✅' : '⚠️'} {geoMensagem.texto}
              </div>
            )}
          </div>
        </div>

        {/* ── Congregação ── */}
        <div className="form-section">
          <h3><FaMapMarkerAlt /> Congregação</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="congregacao">Congregação *</label>
              {loadingCongregacoes ? (
                <div className="loading-select">Carregando congregações...</div>
              ) : (
                <>
                  <select
                    id="congregacao"
                    name="congregacao"
                    value={
                      !formData.congregacao
                        ? ''
                        : congregacoes.some(c => c.nome === formData.congregacao)
                          ? formData.congregacao
                          : '__outra__'
                    }
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      congregacao: e.target.value,
                      ...(e.target.value !== '__outra__' ? { congregacao_outra: '' } : {}),
                    }))}
                    className={erros.congregacao ? 'erro' : ''}
                  >
                    <option value="">Selecione uma congregação</option>
                    {congregacoes.map(c => (
                      <option key={c.id} value={c.nome}>{c.nome}</option>
                    ))}
                    <option value="__outra__">Outra (informar nome)</option>
                  </select>
                  {(formData.congregacao === '__outra__' || (formData.congregacao && !congregacoes.some(c => c.nome === formData.congregacao))) && (
                    <input
                      type="text"
                      name="congregacao_outra"
                      placeholder="Nome da congregação"
                      value={formData.congregacao === '__outra__' ? formData.congregacao_outra : formData.congregacao}
                      className="input-outra-congregacao"
                      style={{ marginTop: 8 }}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        congregacao_outra: e.target.value,
                        congregacao: '__outra__'
                      }))}
                    />
                  )}
                </>
              )}
              {erros.congregacao && <span className="erro-texto">{erros.congregacao}</span>}
            </div>
          </div>
        </div>

        {/* ── Responsáveis (crianças e quem tem até 12 anos) ── */}
        {precisaResp && (
          <div className="form-section">
            <h3><FaUsers /> Responsáveis</h3>
            <div className="form-row">
              <div className="form-group">
                {carregandoResponsaveis ? (
                  <span className="carregando-texto">Carregando…</span>
                ) : (
                  <SeletorResponsaveis
                    potenciais={potenciaisResponsaveis}
                    value={formData.responsaveis}
                    onChange={(novo) => setFormData(prev => ({ ...prev, responsaveis: novo }))}
                    criancaId={id}
                    onMembroAdicionado={(criado) => {
                      if (!criado?.id) return;
                      // Adiciona o novo responsável à lista local de potenciais (para exibir o nome)
                      setPotenciaisResponsaveis(prev => {
                        if (prev.some(p => Number(p.id) === Number(criado.id))) return prev;
                        return [...prev, {
                          id: criado.id,
                          nomeCompleto: criado.nomeCompleto || criado.nome_completo || '',
                          nome_completo: criado.nome_completo || criado.nomeCompleto || '',
                        }];
                      });
                      // Adiciona ao estado local de selecionados (o vínculo já foi salvo no banco)
                      setFormData(prev => {
                        if (prev.responsaveis.some(r => Number(r.id) === Number(criado.id))) return prev;
                        return {
                          ...prev,
                          responsaveis: [
                            ...prev.responsaveis,
                            { id: criado.id, principal: prev.responsaveis.length === 0 },
                          ],
                        };
                      });
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Situação do cadastro ── */}
        <div className="form-section">
          <h3><FaHome /> Situação do cadastro</h3>
          <div className={`situacao-painel${situacao.mudouSe ? ' mudou-se' : ''}`}>
            <div className="situacao-info">
              <span className="situacao-titulo">
                {situacao.mudouSe ? (
                  <><FaExchangeAlt className="situacao-status-mudou" /> <span className="situacao-status-mudou">Mudou-se (inativo)</span></>
                ) : (
                  <><FaCheckCircle className="situacao-status-ativo" /> <span className="situacao-status-ativo">Ativo</span></>
                )}
              </span>
              <span className="situacao-sub">
                {situacao.mudouSe
                  ? 'Não é contado em estatísticas, recitativos, reunião de crianças, visitas nem no mapa. O cadastro continua disponível.'
                  : 'Participa normalmente de listas, recitativos e relatórios.'}
              </span>
            </div>
            {situacao.mudouSe ? (
              <button type="button" className="btn-situacao btn-situacao-reativar"
                onClick={handleReativar} disabled={alterandoSituacao}>
                {alterandoSituacao ? <FaSpinner className="spinner" /> : <FaUndoAlt />} Reativar
              </button>
            ) : (
              <button type="button" className="btn-situacao btn-situacao-mudou"
                onClick={handleMarcarMudouSe} disabled={alterandoSituacao}>
                {alterandoSituacao ? <FaSpinner className="spinner" /> : <FaExchangeAlt />} Marcar como mudou-se
              </button>
            )}
          </div>
        </div>

        <div className="form-section">
          <h3><FaUsers /> Frequência e acompanhamento</h3>
          <span className="campo-dica frequencia-dica">
            Campos opcionais. Use “Não informado” quando ainda não houver confirmação.
          </span>
          <div className="form-row frequencia-tristate-grid">
            {[
              ['comparece_reuniao', 'Comparece à reunião?'],
              ['frequente_reuniao', 'É frequente?'],
              ...(categoria === 'jovem' ? [['batizado', 'Batizado?']] : []),
              ['visita_indicada', 'Visita indicada?'],
              ['aceitou_visita', 'Aceitou visita?'],
            ].map(([campo, label]) => (
              <div className="form-group" key={campo}>
                <label htmlFor={campo}>{label}</label>
                <select
                  id={campo}
                  value={valorTriState(formData[campo])}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    [campo]: parseTriState(e.target.value),
                  }))}
                >
                  <option value="null">Não informado</option>
                  <option value="true">Sim</option>
                  <option value="false">Não</option>
                </select>
              </div>
            ))}
          </div>
        </div>

        {/* ── Cargos ── */}
        {temCargos && (
          <div className="form-section">
            <h3><FaUsers /> Cargos</h3>
            {carregandoCargos ? (
              <div className="carregando-texto">Carregando cargos…</div>
            ) : (
              <div className="cargos-grid">
                {cargos.map(cargo => (
                  <label key={cargo.id} className="cargo-checkbox">
                    <input type="checkbox"
                      checked={formData.cargos_selecionados.includes(cargo.id)}
                      onChange={() => handleCargoChange(cargo.id)} />
                    <span className="cargo-nome">{cargo.nome}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Observações ── */}
        <div className="form-section">
          <h3>Observações</h3>
          <div className="form-row">
            <div className="form-group">
              <textarea id="observacoes" name="observacoes"
                value={formData.observacoes} onChange={handleInputChange}
                rows="3" placeholder="Observações adicionais" />
            </div>
          </div>
        </div>

        {/* ── Documentos ── */}
        <div className="form-section">
          <h3><FaPaperclip /> Documentos</h3>

          {/* Upload */}
          <div className="doc-upload-area">
            <input ref={fileRef} type="file" id="doc-file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
              onChange={handleFileChange}
              style={{ display: 'none' }} />

            <div className="doc-upload-row">
              <button type="button" className="btn-escolher-arquivo"
                onClick={() => fileRef.current?.click()}>
                <FaPlus /> Escolher arquivo
              </button>

              {uploadInfo.arquivo && (
                <>
                  <input
                    type="text"
                    className="doc-nome-input"
                    placeholder="Nome do documento"
                    value={uploadInfo.nome}
                    onChange={e => setUploadInfo(p => ({ ...p, nome: e.target.value }))}
                  />
                  <button type="button" className="btn-salvar-doc"
                    onClick={handleUpload} disabled={enviandoDoc}>
                    {enviandoDoc ? <FaSpinner className="spinner" /> : <FaPlus />}
                    {enviandoDoc ? 'Enviando…' : 'Anexar'}
                  </button>
                  <button type="button" className="btn-cancelar-doc"
                    onClick={() => { setUploadInfo({ nome: '', arquivo: null }); if (fileRef.current) fileRef.current.value = ''; }}>
                    <FaTimes />
                  </button>
                </>
              )}
            </div>

            {uploadInfo.arquivo && (
              <span className="doc-arquivo-escolhido">
                {uploadInfo.arquivo.name} ({formatBytes(uploadInfo.arquivo.size)})
              </span>
            )}

            <span className="campo-dica">PDF, imagem (JPG/PNG) ou Word. Máx. 10 MB.</span>
          </div>

          {/* Lista de documentos */}
          {documentos.length > 0 && (
            <div className="doc-lista">
              {documentos.map(doc => (
                <div key={doc.id} className="doc-item">
                  <span className="doc-icone">{iconeDoc(doc.tipoMime)}</span>
                  <span className="doc-nome">{doc.nome}</span>
                  <span className="doc-tamanho">{formatBytes(doc.tamanho)}</span>
                  <div className="doc-acoes">
                    <button type="button" title="Baixar"
                      className="btn-doc-acao btn-doc-download"
                      onClick={() => handleDownloadDoc(doc.id)}>
                      <FaDownload />
                    </button>
                    <button type="button" title="Excluir"
                      className="btn-doc-acao btn-doc-excluir"
                      onClick={() => handleExcluirDoc(doc.id)}>
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {documentos.length === 0 && (
            <p className="doc-vazio">Nenhum documento anexado.</p>
          )}
        </div>

        {/* ── Histórico de Presenças por Cargo ── */}
        <div className="form-section historico-presencas-section">
          <h3><FaChartBar /> Histórico de Presenças por Cargo</h3>

          {loadingHistorico ? (
            <p className="carregando-texto">Carregando histórico…</p>
          ) : historicoPresencas.length === 0 ? (
            <p className="doc-vazio">Nenhum cargo vinculado ou sem registros de presença ainda.</p>
          ) : (
            <div className="historico-presencas-lista">
              {historicoPresencas.map((h, i) => {
                const p = h.percentual;
                const corBadge = p === null ? '#a1a1aa' : p >= 70 ? '#22c55e' : p >= 50 ? '#eab308' : '#ef4444';
                const fmtData = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : null;
                const periodo = [fmtData(h.dataInicio), fmtData(h.dataFim)].filter(Boolean).join(' – ') || (h.ativo ? 'Em andamento' : 'Sem data');

                return (
                  <div key={i} className="historico-presencas-item">
                    <div className="historico-presencas-cargo">
                      <span className="historico-cargo-nome">{h.cargoNome}</span>
                      {h.ativo && <span className="historico-cargo-badge-ativo">Ativo</span>}
                    </div>
                    <div className="historico-presencas-periodo">{periodo}</div>
                    <div className="historico-presencas-stats">
                      <span className="historico-stat">{h.totalReunioes} reuniões</span>
                      <span className="historico-stat">{h.presentes} presenças</span>
                      <span
                        className="historico-frequencia"
                        style={{ color: corBadge, fontWeight: 700 }}
                      >
                        {p === null ? '—' : `${p}%`}
                      </span>
                    </div>
                    {h.totalReunioes > 0 && (
                      <div className="historico-barra-wrap">
                        <div
                          className="historico-barra-fill"
                          style={{ width: `${p}%`, background: corBadge }}
                        />
                        <div className="historico-barra-meta" style={{ left: '70%' }} title="Meta 70%" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Frequência — Reunião de Jovens ── */}
        <div className="form-section hist-rec-section">
          <h3><FaBookOpen /> Frequência — Reunião de Jovens</h3>

          {loadingHistRec ? (
            <p className="carregando-texto">Carregando histórico…</p>
          ) : !historicoRecitativos || historicoRecitativos.historico.length === 0 ? (
            <p className="doc-vazio">Nenhum recitativo registrado para esta pessoa ainda.</p>
          ) : (() => {
            const { resumo, historico } = historicoRecitativos;
            const corPct = resumo.percentual === null ? '#a1a1aa'
              : resumo.percentual >= 70 ? '#22c55e'
              : resumo.percentual >= 50 ? '#eab308'
              : '#ef4444';
            const nomeTurma = { 1: '1ª turma (3–6 anos)', 2: '2ª turma (7–11 anos)', 3: '3ª turma (12+ anos)' };
            const fmtData = d => d ? new Date(d).toLocaleDateString('pt-BR') : '—';

            return (
              <>
                {/* Resumo */}
                <div className="hist-rec-resumo">
                  <div className="hist-rec-resumo-txt">
                    <span className="hist-rec-presencas">
                      <FaCheckCircle style={{ color: '#22c55e' }} /> {resumo.presentes} presenças
                    </span>
                    <span className="hist-rec-ausencias">
                      <FaTimesCircle style={{ color: '#ef4444' }} /> {resumo.ausentes} ausências
                    </span>
                    {resumo.sem_registro > 0 && (
                      <span className="hist-rec-semreg">
                        <FaClock style={{ color: '#a1a1aa' }} /> {resumo.sem_registro} sem registro
                      </span>
                    )}
                    <span className="hist-rec-pct" style={{ color: corPct }}>
                      {resumo.percentual === null ? '—' : `${resumo.percentual}% de frequência`}
                    </span>
                  </div>
                  {resumo.percentual !== null && (
                    <div className="hist-rec-barra-wrap">
                      <div className="hist-rec-barra-fill" style={{ width: `${resumo.percentual}%`, background: corPct }} />
                      <div className="hist-rec-barra-meta" title="Meta 70%" />
                    </div>
                  )}
                </div>

                {/* Tabela / lista */}
                <div className="hist-rec-lista">
                  {historico.map(e => {
                    const icone = e.presente === true
                      ? <FaCheckCircle style={{ color: '#22c55e' }} />
                      : e.presente === false
                        ? <FaTimesCircle style={{ color: '#ef4444' }} />
                        : <FaClock style={{ color: '#a1a1aa' }} />;
                    const textoPresenca = e.presente === true ? 'Presente'
                      : e.presente === false ? 'Ausente'
                      : 'Não marcado';
                    return (
                      <div key={e.entrega_id} className="hist-rec-item">
                        <div className="hist-rec-item-data">{fmtData(e.data_referencia)}</div>
                        <div className="hist-rec-item-info">
                          <span className="hist-rec-ref">{e.referencia}</span>
                          <span className="hist-rec-detalhes">
                            {nomeTurma[e.turma] || `Turma ${e.turma}`} · {e.localidade} · Aux: {e.auxiliar || '—'}
                          </span>
                        </div>
                        <div className="hist-rec-item-status">
                          {icone} <span className="hist-rec-status-txt">{textoPresenca}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>

        {/* ── Frequência — Reunião de Crianças ── */}
        {categoria === 'crianca' && (
          <div className="form-section hist-rec-section">
            <h3><FaChartBar /> Frequência — Reunião de Crianças</h3>
            {loadingFreqCriancas ? (
              <p className="carregando-texto">Carregando frequência...</p>
            ) : !freqCriancas || freqCriancas.resumo.totalReunioes === 0 ? (
              <p className="doc-vazio">Nenhuma reunião de crianças registrada ainda.</p>
            ) : (() => {
              const { resumo, historico } = freqCriancas;
              const pct = resumo.percentual;
              const cor = pct >= 70 ? '#22c55e' : pct >= 50 ? '#eab308' : '#ef4444';
              const fmtData = d => d ? new Date(d).toLocaleDateString('pt-BR') : '-';
              return (
                <>
                  <div className="hist-rec-resumo">
                    <div className="hist-rec-resumo-txt">
                      <span className="hist-rec-presencas">
                        <FaCheckCircle style={{ color: '#22c55e' }} /> {resumo.totalPresencas} presenças
                      </span>
                      <span className="hist-rec-ausencias">
                        <FaTimesCircle style={{ color: '#ef4444' }} /> {resumo.ausencias} ausências
                      </span>
                      <span className="hist-rec-pct" style={{ color: cor }}>
                        {pct}% de frequência ({resumo.totalReunioes} reuniões)
                      </span>
                    </div>
                    <div className="hist-rec-barra-wrap">
                      <div className="hist-rec-barra-fill" style={{ width: `${pct}%`, background: cor }} />
                      <div className="hist-rec-barra-meta" title="Meta 70%" />
                    </div>
                  </div>
                  {historico.length > 0 && (
                    <div className="hist-rec-lista">
                      {historico.slice(0, 20).map((h, i) => (
                        <div key={i} className="hist-rec-item">
                          <div className="hist-rec-item-data">{fmtData(h.data)}</div>
                          <div className="hist-rec-item-info">
                            <span className="hist-rec-ref">{h.congregacao}</span>
                            <span className="hist-rec-detalhes">{h.totalNaReuniao} crianças na reunião</span>
                          </div>
                          <div className="hist-rec-item-status">
                            <FaCheckCircle style={{ color: '#22c55e' }} />
                            <span className="hist-rec-status-txt">Presente</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* ── Histórico de Alterações ── */}
        <div className="form-section hist-rec-section">
          <h3
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
            onClick={() => setLogsExpandido(p => !p)}
          >
            <FaHistory /> Histórico de Alterações
            <span style={{ fontSize: 12, color: '#71717a', fontWeight: 400, marginLeft: 'auto' }}>
              {logsAlteracao.length} registro(s) {logsExpandido ? '▲' : '▼'}
            </span>
          </h3>

          {logsExpandido && (
            loadingLogs ? (
              <p className="carregando-texto">Carregando histórico...</p>
            ) : logsAlteracao.length === 0 ? (
              <p className="doc-vazio">Nenhuma alteração registrada.</p>
            ) : (
              <div className="logs-alteracao-lista">
                {logsAlteracao.map(log => {
                  const data = new Date(log.createdAt).toLocaleString('pt-BR', {
                    day: '2-digit', month: '2-digit', year: 'numeric',
                    hour: '2-digit', minute: '2-digit'
                  });
                  const acaoLabel = log.acao === 'criacao' ? 'Criou o cadastro'
                    : log.acao === 'exclusao' ? 'Excluiu o cadastro'
                    : `Alterou "${log.campo}"`;
                  const acaoCor = log.acao === 'criacao' ? '#22c55e'
                    : log.acao === 'exclusao' ? '#ef4444'
                    : '#3b82f6';

                  return (
                    <div key={log.id} className="log-item" style={{ borderLeft: `3px solid ${acaoCor}` }}>
                      <div className="log-header">
                        <span className="log-usuario">{log.usuarioNome || 'Sistema'}</span>
                        <span className="log-data">{data}</span>
                      </div>
                      <div className="log-acao" style={{ color: acaoCor }}>{acaoLabel}</div>
                      {log.acao === 'alteracao' && (
                        <div className="log-valores">
                          <span className="log-antes">{log.valorAnterior || '(vazio)'}</span>
                          <span className="log-seta">→</span>
                          <span className="log-depois">{log.valorNovo || '(vazio)'}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>

        {/* ── Ações ── */}
        <div className="form-actions">
          <button type="button" className="btn-cancelar" onClick={() => navigate('/irmandade')}>
            <FaTimes /> Cancelar
          </button>
          <button type="submit" className="btn-salvar" disabled={carregando}>
            {carregando ? <FaSpinner className="spinner" /> : <FaSave />}
            {carregando ? 'Salvando…' : 'Salvar Alterações'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditarIrmandade;
